import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ModalHeaderComponent } from 'src/app/shared/modals/components/modal-header/modal-header.component';
import { Modal } from 'src/app/shared/modals/modal';
import { IStartAtEndAtFG, StartAtEndAtInputComponent, getStartAtEndAtFG } from '../session-card-modal/start-at-end-at-input/start-at-end-at-input.component';
import { Subject, Subscription, take } from 'rxjs';
import { ILocation } from 'src/app/shared/models/location/location.model';
import { OrgLocationsStoreService } from 'src/app/shared/services/store/org-locations-store.service';
import { ListboxModule } from 'primeng/listbox';
import { ICallablesSessionsGetForLocationPickerResult, SessionsService } from 'src/app/shared/services/entities/sessions/sessions.service';
import { ITimelineSessionChange, TIMELINE_newLocationId, TimelineComponent } from './timeline/timeline.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { dateToTimeString, timeStringToDate } from 'src/app/shared/components/time-input/time-input.component';
import { ISession } from 'src/app/shared/models/session-group/session.model';
import { IShopItem } from 'src/app/shared/models/shop-item/shop-item.model';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { SelectedOrgService } from 'src/app/shared/services/selected-org.service';
import { IUserPreferencesData } from 'src/app/shared/models/user-preference/user-preference.model';
import { UserPreferencesStoreService } from 'src/app/shared/services/store/user-preferences-store.service';

export type ILocationPickerModalResult = Subject<{
  locationId: number | null,
  dates: { startAt: Date | null, endAt: Date | null, startAtTime: string, endAtTime: string },
}>;

@Component({
  selector: 'app-location-picker-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule, TranslateModule, AsyncPipe,
    InputTextModule, ButtonModule, ListboxModule, ProgressSpinnerModule,
    ModalHeaderComponent, StartAtEndAtInputComponent, TimelineComponent,
  ],
  templateUrl: './location-picker-modal.component.html',
  styleUrl: './location-picker-modal.component.scss'
})
export class LocationPickerModalComponent extends Modal implements OnInit, OnDestroy {

  _datesFG: FormGroup<IStartAtEndAtFG> = getStartAtEndAtFG();
  get datesFG() { return this._datesFG; }
  @Input({required: true}) set datesFG(val: FormGroup<IStartAtEndAtFG>) { this._datesFG.patchValue(val.value, { emitEvent: false }); }
  _locationIdFC: FormControl<number | null> = new FormControl(null, { validators: [ Validators.required ]});
  get locationIdFC() { return this._locationIdFC; }
  @Input({required: true}) set locationIdFC(val: FormControl<number | null>) { this._locationIdFC.setValue(val.value, { emitEvent: false }); }
  @Input() session: ISession | undefined;
  @Input() shopItem: IShopItem | undefined;

  @Output() result: ILocationPickerModalResult = new Subject();

  searchFC = new FormControl<string>('', { nonNullable: true });


  locationsState = this.locationsStore.getState();
  locationsData: ICallablesSessionsGetForLocationPickerResult | null = null;
  locations: ILocation[] = [];
  _filteredLocations: ILocation[] = [];
  get filteredLocations() { return this._filteredLocations; }
  set filteredLocations(val: ILocation[]) { this._filteredLocations = val; this.filteredLocationIds = val.map(location => location.id); };
  filteredLocationIds: number[] = [];
  selectedDate: Date | null | undefined;

  userPreferences: Partial<IUserPreferencesData> | null = null;

  datesChanged$ = new Subject<{ startAt: Date, endAt: Date }>();
  locationChanged$ = new Subject<number | null>();

  subs: Subscription[] = [];

  constructor(
    private locationsStore: OrgLocationsStoreService,
    public selectedOrg: SelectedOrgService,
    private userPreferencesStore: UserPreferencesStoreService,
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // if (!this.locationsStore.inited) this.locationsStore.init();
    this.locationsStore.init();

    this.selectedDate = timeStringToDate(this.datesFG.value.startAt!, this.datesFG.value.startAtTime!);

    this.subs.push(
      this.locationsState.locations$.subscribe(locations => {
        this.locations = locations;
        this.filteredLocations = locations;
      }),

      this.searchFC.valueChanges.subscribe(search => {
        this.filteredLocations = this.locations.filter(location => (location.internalTitle || location.title).toLowerCase().includes(search.toLowerCase()));
      }),

      this.datesFG.valueChanges.subscribe(dates => {
        if (this.datesFG.invalid || !dates.startAt || !dates.endAt || !dates.startAtTime || !dates.endAtTime) return;
        this.datesChanged$.next({
          startAt: timeStringToDate(dates.startAt, dates.startAtTime)!,
          endAt: timeStringToDate(dates.endAt, dates.endAtTime)!,
        });

        this.selectedDate = timeStringToDate(dates.startAt, dates.startAtTime);
      }),
      this.locationIdFC.valueChanges.subscribe(locationId => {
        this.locationChanged$.next(locationId);
      }),

      this.userPreferencesStore.state.preferences$.subscribe(preferences => {
        this.userPreferences = preferences;
      })
    );


  }

  submit() {
    this.result.next({
      locationId: this.locationIdFC.value === TIMELINE_newLocationId ? null : this.locationIdFC.value,
      dates: this.datesFG.getRawValue(),
    });
    this.close();
  }



  onSessionChange(e: ITimelineSessionChange) {
    this.datesFG.patchValue({
      startAt: e.startAt,
      endAt: e.endAt,
      startAtTime: dateToTimeString(e.startAt)!,
      endAtTime: dateToTimeString(e.endAt)!,
    }, { emitEvent: false });

    this.locationIdFC.setValue(e.locationId, { emitEvent: false });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
